import { Box, Divider, FormControl, MenuItem, Paper, Select, Typography, useTheme, Button } from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";
import { PRODUCT_TYPES, getCategoriesForType, getSubCategoriesForType } from "../../data/lciMaterialTypes";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import DonutChart from "./DonutChart";
import HorizontalStackedBarChart from "./HorizontalStackedBarChart";
import PortfolioStatsCard from "../stats/PortfolioStatsCard";
import ChartStudio from "./ChartStudio";

// Matches Ag-Grid cell typography exactly
const cellTextSx = (isDark) => ({
  fontFamily: GRAPH_PALETTE.font.family,
  fontSize: GRAPH_PALETTE.font.sizes.standard,
  fontWeight: GRAPH_PALETTE.font.weights.standard,
  color: isDark ? "#f1f5f9" : "#1e293b",
});

const filterSelectSx = (isDark) => ({
  ...cellTextSx(isDark),
  minWidth: 140,
  "& .MuiSelect-select": { py: "4px", px: 0, pb: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  borderBottom: `2px solid #659d33`,
  borderRadius: 0,
  bgcolor: "transparent",
});

const COLORS = ["#22c55e", "#3b82f6", "#f97316", "#a855f7", "#ec4899", "#14b8a6", "#eab308"];

const DashboardGlobalFootprint = ({ items = [], type = "product" }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);

  const [filterType, setFilterType] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterSubCategory, setFilterSubCategory] = useState("All");
  const [detailedComparison, setDetailedComparison] = useState([]);

  // Fetch detailed A1-D breakdown dynamically for horizontal stacks
  useEffect(() => {
    const fetchComparison = async () => {
      const top15Ids = [...items]
        .filter((item) => (item.gwp || 0) > 0)
        .sort((a, b) => (b.gwp || 0) - (a.gwp || 0))
        .slice(0, 15)
        .map(i => i.id)
        .filter(Boolean);

      if (top15Ids.length === 0) return;

      try {
        let data = [];
        if (type === "product") {
          const res = await Product.get_comparison_gwp_by_stage(top15Ids);
          data = await res.json();
        } else if (type === "project") {
          const res = await Project.get_total_gwp_by_stage(top15Ids);
          data = await res.json();
        }
        setDetailedComparison(data);
      } catch (err) {
        console.error("Failed to fetch detailed stage distribution", err);
      }
    };
    fetchComparison();
  }, [items, type]);

  const categoryOptions = useMemo(
    () => (filterType !== "All" ? getCategoriesForType(filterType) : []),
    [filterType]
  );
  const subCategoryOptions = useMemo(
    () => (filterType !== "All" && filterCategory !== "All" ? getSubCategoriesForType(filterType, filterCategory) : []),
    [filterType, filterCategory]
  );

  const handleTypeChange = (e) => {
    setFilterType(e.target.value);
    setFilterCategory("All");
    setFilterSubCategory("All");
  };
  const handleCategoryChange = (e) => {
    setFilterCategory(e.target.value);
    setFilterSubCategory("All");
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (filterType !== "All" && item.type !== filterType) return false;
      if (filterCategory !== "All" && item.category !== filterCategory) return false;
      if (filterSubCategory !== "All" && item.sub_category !== filterSubCategory) return false;
      return true;
    });
  }, [items, filterType, filterCategory, filterSubCategory]);

  const chartData = useMemo(() => {
    return [...filteredItems]
      .filter((item) => (item.gwp || 0) > 0)
      .sort((a, b) => (b.gwp || 0) - (a.gwp || 0))
      .slice(0, 15)
      .map((item, index) => {
        let name = item.name || `Item ${index + 1}`;
        if (name.length > 50) name = name.substring(0, 50) + "...";

        const comp = detailedComparison.find(d => String(d.product_id) === String(item.id) || String(d.project_id) === String(item.id));

        if (comp) {
          const a1a3 = Number(comp.A1_A3 || 0);
          const a4a5 = Number(comp.A4_A5 || 0);
          const b = Number(comp.B || 0);
          const c = Number(comp.C || 0);
          const d = Number(comp.D || 0);
          const stageSum = a1a3 + a4a5 + b + c + d;
          // Only use stage breakdown if at least one stage has real data
          if (stageSum > 0) {
            return { label: name, Emissions: item.gwp || 0, "A1-A3": a1a3, "A4-A5": a4a5, "B": b, "C": c, "D": d };
          }
        }
        // Fallback: no stage breakdown available — display total GWP only
        return { label: name, Emissions: item.gwp || 0 };
      });
  }, [filteredItems, detailedComparison]);

  const a1a3ChartData = useMemo(() => {
    return chartData
      .filter((item) => Number(item["A1-A3"]) > 0)
      .map((item) => ({ label: item.label, "A1-A3": Number(item["A1-A3"]) }));
  }, [chartData]);

  const groupTotals = useMemo(() => {
    const totals = {};
    filteredItems.forEach((item) => {
      const key = item.category || "Uncategorized";
      const val = Number(item.gwp || 0) * (item.quantity ? Number(item.quantity) : 1);
      totals[key] = (totals[key] || 0) + val;
    });
    const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    if (sorted.length > 5) {
      const top5 = sorted.slice(0, 5);
      const otherSum = sorted.slice(5).reduce((acc, curr) => acc + curr[1], 0);
      if (otherSum > 0) top5.push(["Other", otherSum]);
      return top5;
    }
    return sorted;
  }, [filteredItems]);

  const totalPortfolio = useMemo(() => groupTotals.reduce((a, b) => a + b[1], 0), [groupTotals]);

  const donutData = useMemo(() =>
    groupTotals
      .map(([label, value], i) => ({
        label,
        value,
        color: COLORS[i % COLORS.length],
        percent: totalPortfolio > 0 ? (value / totalPortfolio) * 100 : 0,
      }))
      .filter((d) => d.value > 0),
    [groupTotals, totalPortfolio]
  );

  const costRatioChartData = useMemo(() => {
    return [...filteredItems]
      .filter((item) => (Number(item.gwp) || 0) > 0 && (Number(item.cost) || 0) > 0)
      .map((item, index) => {
        let name = item.name || `Item ${index + 1}`;
        if (name.length > 50) name = name.substring(0, 50) + "...";
        const gwp = Number(item.gwp) || 1; // avoid div by zero
        const cost = Number(item.cost) || 0;
        return {
          label: name,
          "$/kg CO₂e": cost / gwp,
        };
      })
      .sort((a, b) => b["$/kg CO₂e"] - a["$/kg CO₂e"])
      .slice(0, 15);
  }, [filteredItems]);

  const filteredMean = useMemo(() => {
    if (filteredItems.length === 0) return 0;
    const total = filteredItems.reduce((acc, item) => acc + (Number(item.gwp) || 0), 0);
    return total / filteredItems.length;
  }, [filteredItems]);

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, animation: "org-fadeInUp 0.4s ease" }}>

      {/* ═══════════════════════════════════════════════════════════════════
          LEFT: Bar chart 
      ════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: type === "project" ? "1 1 100%" : "1 1 60%", minWidth: 450 }}>
        <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, display: "flex", flexDirection: "column" }}>

          <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
              {t("Emissions Overview")}
            </Typography>

            {/* Filter Dropdowns */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControl variant="outlined" size="small">
                <Select value={filterType} onChange={handleTypeChange} sx={filterSelectSx(isDark)} displayEmpty>
                  <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Types")}</MenuItem>
                  {PRODUCT_TYPES.map((tp) => (
                    <MenuItem key={tp} value={tp} sx={cellTextSx(isDark)}>{tp}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {categoryOptions.length > 0 && (
                <FormControl variant="outlined" size="small">
                  <Select value={filterCategory} onChange={handleCategoryChange} sx={filterSelectSx(isDark)} displayEmpty>
                    <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Categories")}</MenuItem>
                    {categoryOptions.map((c) => (
                      <MenuItem key={c} value={c} sx={cellTextSx(isDark)}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {subCategoryOptions.length > 0 && (
                <FormControl variant="outlined" size="small">
                  <Select value={filterSubCategory} onChange={(e) => setFilterSubCategory(e.target.value)} sx={filterSelectSx(isDark)} displayEmpty>
                    <MenuItem value="All" sx={cellTextSx(isDark)}>{t("All Sub Categories")}</MenuItem>
                    {subCategoryOptions.map((s) => (
                      <MenuItem key={s} value={s} sx={cellTextSx(isDark)}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>

          {type === "product" ? (
            // Products: Single-series A1-A3 only — log scale is valid for single bars
            <HorizontalStackedBarChart
              data={a1a3ChartData}
              dataKeys={["A1-A3"]}
              getColor={() => "#ef4444"}
              showLegend={false}
              scale="log"
              domain={[0.0001, "auto"]}
              height={Math.max(a1a3ChartData.length * 45 + 100, 350)}
              barSize={28}
              xAxisLabel={t("A1-A3 Emissions (kg CO2e) - Log Scale")}
              yAxisLabel={t("Products")}
              showMean={true}
              customMeanValue={filteredMean}
              meanLabel={t("Portfolio Mean")}
              fillHeight={true}
            />
          ) : (
            // Projects: All stages stacked — linear scale is mathematically correct for stacks
            <HorizontalStackedBarChart
              data={chartData}
              dataKeys={chartData.some(d => d["A1-A3"] !== undefined) ? ["A1-A3", "A4-A5", "B", "C", "D"] : ["Emissions"]}
              getColor={(i) => {
                const stageColors = ["#ef4444", "#22c55e", "#14b8a6", "#ec4899", "#64748b"];
                return stageColors[i % stageColors.length];
              }}
              showLegend={chartData.some(d => d["A1-A3"] !== undefined)}
              scale="auto"
              domain={[0, "auto"]}
              fillHeight={true}
              height={Math.max(chartData.length * 45 + 100, 450)}
              barSize={38}
              xAxisLabel={t("Total Emissions (kg CO2e)")}
              yAxisLabel={t("Projects")}
              showMean={true}
              customMeanValue={filteredMean}
              meanLabel={t("Portfolio Mean")}
            />
          )}

          {type === "project" && (
            <Box sx={{ mt: 4, flexShrink: 0 }}>
              <Divider sx={{ mb: 3, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", mb: 2, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("Portfolio Statistics")}
              </Typography>
              <PortfolioStatsCard items={filteredItems} isDark={isDark} tok={tok} direction="row" hideHeader={true} />
            </Box>
          )}
        </Paper>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════
          RIGHT: Donut + portfolio stats (Hidden if type === "project")
      ════════════════════════════════════════════════════════════════════ */}
      {type !== "project" && (
        <Box sx={{ flex: "1 1 30%", minWidth: 350 }}>
          <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "100%", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card }}>
            <Box sx={{ mb: 3, pb: 1, borderBottom: `1px solid ${tok.border.divider}` }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
                {t("GWP Distribution")}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <DonutChart
                data={donutData}
                size={200}
                strokeWidth={22}
                centerContent={(
                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ ...cellTextSx(isDark), display: "block", color: isDark ? "#94a3b8" : "#64748b" }}>{t("Total")}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: tok.text.primary, lineHeight: 1.2 }}>
                      {totalPortfolio.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                    <Typography sx={{ ...cellTextSx(isDark), color: isDark ? "#94a3b8" : "#64748b" }}>{t("kg CO2e")}</Typography>
                  </Box>
                )}
              />
            </Box>

            {/* Legend */}
            <Box sx={{ pr: 1 }}>
              {donutData.map((d, i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>{t(d.label)}</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.axisLabel, fontWeight: GRAPH_PALETTE.font.weights.standard, color: tok.text.primary }}>
                      {d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({Math.round(d.percent)}%)
                    </Typography>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                  </Box>
                </Box>
              ))}
            </Box>
            <Divider sx={{ mt: 3, mb: 2.5, opacity: 0.35 }} />

            {/* Portfolio Stats */}
            <PortfolioStatsCard items={filteredItems} isDark={isDark} tok={tok} />
          </Paper>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          BOTTOM: Cost Efficiency ($/kg) Bar Chart
      ════════════════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: "1 1 100%", width: "100%", mt: 1 }}>
        <Paper variant="Chart Container" sx={{ p: ORG_PALETTE.spacing.chartPad, height: "auto", borderRadius: ORG_PALETTE.radius.card, border: "1px solid", borderColor: tok.border.card, bgcolor: tok.bg.card, display: "flex", flexDirection: "column" }}>
          <Box sx={{ mb: 2, pb: 1, borderBottom: `1px solid ${tok.border.divider}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: 500, color: tok.text.chartTitle, textAlign: "left", m: 0, fontFamily: GRAPH_PALETTE.font.family }}>
              {t("Cost Efficiency Analysis")}
            </Typography>
          </Box>

          {costRatioChartData.length > 0 ? (
            <HorizontalStackedBarChart
              data={costRatioChartData}
              dataKeys={["$/kg CO₂e"]}
              getColor={() => "#f97316"}
              showLegend={false}
              scale="log"
              domain={[0.0001, "auto"]}
              height={Math.max(costRatioChartData.length * 45 + 100, type === "product" ? 390 : 490)}
              barSize={38}
              xAxisLabel={t("Cost Efficiency ($/kg CO₂e) - Log Scale")}
              yAxisLabel={type === "product" ? t("Products") : t("Projects")}
            />
          ) : (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', opacity: 0.5 }}>
              <Typography sx={cellTextSx(isDark)}>{t("No cost/GWP data available.")}</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════
          CHART STUDIO: Integrated workspace at the bottom (SUPPRESSED as requested)
      ════════════════════════════════════════════════════════════════════ */}
      {/* <Box sx={{ flex: "1 1 100%", width: "100%", mt: 2 }}>
        <ChartStudio items={filteredItems} entityLabel={type === "product" ? "Products" : "Projects"} />
      </Box> */}

    </Box>
  );
};

export default DashboardGlobalFootprint;
